import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

/*
 * "Abstract" skin — a developer-terminal aesthetic built on the reference color
 * mockup: a violet/indigo primary with teal, blue, green, amber, red and purple
 * ramps for status, and neutral greys for surfaces.
 *
 * Typography is a site-aligned split: Work Sans for UI and prose, Funnel Display for headings, JetBrains Mono for
 * code, identifiers and numerics. Mono for body text costs real readability
 * (wider advance width, more wrapping), and reserving it for identifiers is
 * what makes it read as intentional rather than as a skin.
 *
 * We pass an explicit `typography` object rather than the top-level
 * `fontFamily` shortcut: createBaseThemeOptions MUTATES the module-level
 * defaultTypography singleton when given `fontFamily`, so two themes created
 * with different fonts would race and last-created would win for both.
 */

// Exported so components can reach for mono deliberately — entity refs, IDs,
// code snippets, and any tabular numerics.
export const MONO =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const SANS =
  "'Work Sans', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Violet primary ramp (mockup rows 264/2003) — the brand accent.
// Brand tokens from abstract.it: primary #7355ec, secondary #ff7800, ink #020202,
// dark #1b162d, surface #f4f4f4. Headings use Funnel Display, body Work Sans.
const HEADING =
  "'Funnel Display', 'Work Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";
const VIOLET = '#7355ec';
const VIOLET_DARK = '#4f33c4';
const VIOLET_LIGHT = '#8f77f0';
const INDIGO = '#1b162d';

// Semantic accents — shared across light/dark.
const TEAL = '#00a39c';
const BLUE = '#007aff';
const GREEN = '#18aa3d';
const GREEN_DARK = '#43bb61';
const AMBER = '#ffcc00';
const ORANGE = '#ff7800';
const RED = '#f40501';
const RED_DARK = '#f86967';
const PURPLE = '#b63bf5';

// AA-safe variants for text/icon use on light surfaces. The raw ramp colours
// above stay as-is because pageTheme gradients depend on them; these are the
// ones wired into the light palette's semantic slots. Each is >= 4.5:1 against
// both #ffffff (paper) and #f8f8f8 (default background).
const TEAL_AA = '#00706b';
const GREEN_AA = '#0f7a2b';
const ORANGE_AA = '#a34d00';
const AMBER_AA = '#7a6200';
// Lifted for dark surfaces: the originals sat at 4.37:1 and 3.77:1 on #252628.
const VIOLET_LIFTED = '#a898f5';
const BLUE_LIFTED = '#5aa9ff';

const pageTheme = {
  home: genPageTheme({ colors: [VIOLET, INDIGO], shape: shapes.wave }),
  app: genPageTheme({ colors: [VIOLET_DARK, INDIGO], shape: shapes.wave }),
  apis: genPageTheme({ colors: [BLUE, '#004999'], shape: shapes.wave2 }),
  service: genPageTheme({ colors: [TEAL, '#003d3a'], shape: shapes.wave }),
  website: genPageTheme({ colors: [PURPLE, VIOLET], shape: shapes.wave }),
  library: genPageTheme({ colors: ['#3a38f4', INDIGO], shape: shapes.wave }),
  tool: genPageTheme({
    colors: [VIOLET_LIGHT, VIOLET_DARK],
    shape: shapes.round,
  }),
  documentation: genPageTheme({
    colors: [PURPLE, VIOLET_DARK],
    shape: shapes.wave2,
  }),
  other: genPageTheme({ colors: ['#252628', '#191b1d'], shape: shapes.wave }),
  card: genPageTheme({ colors: [GREEN, GREEN_DARK], shape: shapes.wave }),
};

const typography = {
  htmlFontSize: 16,
  fontFamily: SANS,
  h1: {
    fontFamily: HEADING,
    fontSize: 54,
    fontWeight: 700,
    marginBottom: 10,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: HEADING,
    fontSize: 40,
    fontWeight: 700,
    marginBottom: 8,
    letterSpacing: '-0.02em',
  },
  h3: {
    fontFamily: HEADING,
    fontSize: 32,
    fontWeight: 700,
    marginBottom: 6,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: HEADING,
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 6,
    letterSpacing: '-0.01em',
  },
  h5: { fontFamily: HEADING, fontSize: 24, fontWeight: 700, marginBottom: 4 },
  h6: { fontFamily: HEADING, fontSize: 20, fontWeight: 700, marginBottom: 2 },
};

export const abstractDark = createUnifiedTheme({
  typography,
  defaultPageTheme: 'home',
  pageTheme,
  palette: {
    ...palettes.dark,
    background: { default: '#1b162d', paper: '#262040' },
    primary: {
      ...palettes.dark.primary,
      main: VIOLET_LIGHT,
      dark: VIOLET_DARK,
      light: '#c7bbf7',
    },
    secondary: { main: ORANGE },
    error: { main: RED_DARK },
    warning: { main: ORANGE },
    success: { main: GREEN_DARK },
    info: { main: BLUE_LIFTED },
    text: { primary: '#ffffff', secondary: '#c2c2c2' },
    divider: '#ffffff1a',
    status: {
      ok: GREEN_DARK,
      warning: AMBER,
      error: RED_DARK,
      running: TEAL,
      pending: AMBER,
      aborted: '#959595',
    },
    link: VIOLET_LIFTED,
    linkHover: '#c7bbf7',
    navigation: {
      ...palettes.dark.navigation,
      background: '#120e22',
      indicator: VIOLET_LIGHT,
      color: '#b3b3b3',
      selectedColor: '#ffffff',
      navItem: { hoverBackground: '#ffffff0d' },
      submenu: { background: '#120e22' },
    },
    tabbar: {
      indicator: VIOLET_LIGHT,
    },
  },
});

export const abstractLight = createUnifiedTheme({
  typography,
  defaultPageTheme: 'home',
  pageTheme,
  palette: {
    ...palettes.light,
    background: { default: '#f4f4f4', paper: '#ffffff' },
    primary: {
      ...palettes.light.primary,
      main: VIOLET,
      dark: VIOLET_DARK,
      light: VIOLET_LIGHT,
    },
    secondary: { main: ORANGE_AA },
    error: { main: RED },
    warning: { main: ORANGE_AA },
    success: { main: GREEN_AA },
    info: { main: BLUE },
    text: { primary: '#020202', secondary: '#4b4b4b' },
    divider: '#191b1d1f',
    status: {
      ok: GREEN_AA,
      warning: ORANGE_AA,
      error: RED,
      running: TEAL_AA,
      pending: AMBER_AA,
      aborted: '#787878',
    },
    link: VIOLET,
    linkHover: VIOLET_DARK,
    navigation: {
      ...palettes.light.navigation,
      background: '#120e22',
      indicator: VIOLET_LIGHT,
      color: '#b3b3b3',
      selectedColor: '#ffffff',
      navItem: { hoverBackground: '#ffffff0d' },
      submenu: { background: '#1b162d' },
    },
    tabbar: {
      indicator: VIOLET,
    },
  },
});
